import { createGlLayer } from "./gl/glLayer";
import { createLineRenderer } from "./gl/lineRenderer";
import { createRectRenderer } from "./gl/rectRenderer";
import { createTextLayer } from "./text/textLayer";
import { loadChartFonts } from "./text/loadFonts";
import { createTextFrame } from "./textFrame";
import { createPulseRing } from "./overlays/pulseRing";
import { createMainSeries } from "./series/mainSeries";
import { createIndicators } from "./indicators/createIndicators";
import { createPaneStack } from "./indicators/paneStack";
import { createDrawings } from "./drawings/createDrawings";

/**
 * Drawing layers of the engine in `container`, bottom up (plan §3): the
 * WebGL layer with its renderers (grid, position zones, the main series,
 * lines above it, the drawings of the user, the oscillator pane grid;
 * indicators add theirs), the text
 * layer and the pulse ring of the Line dot. The crosshair layer is added by
 * the interaction. `paneStack` places the oscillator panes. Text is measured and drawn again once the web fonts have
 * loaded. `shownBars` passes the shown bars (animator) to the series and
 * the indicators.
 *
 * @param {object} engine - ChartEngine: theme, invalidate(); the text frame draws it
 * @param {HTMLElement} container
 */
export const createLayers = (engine, container) => {
  const { colors } = engine.theme;
  const gridLines = createLineRenderer();
  const positionZones = createRectRenderer();
  const series = createMainSeries(colors);
  const overlayLines = createLineRenderer();
  const paneGridLines = createLineRenderer();
  const invalidate = () => engine.invalidate();
  const drawings = createDrawings({
    theme: engine.theme,
    getView: () => ({
      store: engine.data.store,
      barDurationMs: engine.data.barDurationMs,
      timeScale: engine.timeScale,
      priceScale: engine.priceScale,
      chartWidth: engine.size?.width,
    }),
    invalidate,
    redrawText: () => engine.textFrame.redraw(),
    storage: engine.drawingsStorage,
  });
  const glLayer = createGlLayer(container, {
    renderers: [gridLines, positionZones, ...series.renderers, overlayLines, ...drawings.renderers, paneGridLines],
    onRestored: invalidate,
  });
  const indicators = createIndicators({
    glLayer,
    theme: engine.theme,
    invalidate,
    getPriceStep: () => engine.data.priceFormat.minMove,
  });
  const paneStack = createPaneStack(invalidate);
  const textLayer = createTextLayer(container);
  const textFrame = createTextFrame(engine);
  const pulseRing = createPulseRing(container, colors.lineSeries.css);

  loadChartFonts(engine.theme.fontFamily, () => {
    textLayer.resetMeasurements();
    engine.invalidate();
  });

  return {
    gridLines,
    positionZones,
    series,
    indicators,
    drawings,
    paneStack,
    overlayLines,
    paneGridLines,
    glLayer,
    textLayer,
    textFrame,
    pulseRing,

    shownBars: {
      /** @param {boolean} isNewBar - one bar was added on the right */
      setBars: (bars, isNewBar) => {
        series.setBars(bars);
        indicators.setBars(bars, isNewBar);
      },
      updateLastBar: (bars) => {
        series.updateLastBar(bars);
        indicators.updateLastBar(bars);
      },
    },

    destroyLayers: () => {
      indicators.destroy();
      drawings.destroy();
      glLayer.destroy();
      textFrame.destroy();
      textLayer.destroy();
      pulseRing.destroy();
    },
  };
};
